import React from 'react';
import { Zap, Shield, TrendingUp, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface CaseData {
  industry: string;
  company: string;
  icon: React.ReactNode;
  challenge: string;
  solution: string;
  result: string;
  metrics: Array<{
    label: string;
    value: string;
  }>;
  color: string;
}

const Cases: React.FC = () => {
  const navigate = useNavigate();
  const cases: CaseData[] = [
    {
      industry: '生物医药',
      company: '某知名制药企业',
      icon: <Zap className="w-6 h-6" />,
      challenge: '分子动力学模拟需要大量计算资源，传统本地服务器无法满足高通量计算需求。',
      solution: '部署ChainCloud云超算平台，利用弹性计算资源进行大规模分子动力学模拟。',
      result: '计算效率提升60%，研发周期缩短40%，成本降低50%。',
      metrics: [
        { label: '计算效率提升', value: '60%' },
        { label: '研发周期缩短', value: '40%' },
        { label: '成本降低', value: '50%' }
      ],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      industry: '汽车制造',
      company: '某汽车制造商',
      icon: <Shield className="w-6 h-6" />,
      challenge: '电磁场仿真计算复杂，需要大量CPU资源，项目周期紧张。',
      solution: '使用ChainCloud云超算平台的CAE/CFD解决方案，实现高效的电磁场仿真。',
      result: '仿真精度提升30%，计算时间缩短70%，成功完成关键项目。',
      metrics: [
        { label: '仿真精度提升', value: '30%' },
        { label: '计算时间缩短', value: '70%' },
        { label: '项目按时完成', value: '100%' }
      ],
      color: 'from-purple-500 to-pink-500'
    },
    {
      industry: '新药研发',
      company: '某生物技术公司',
      icon: <TrendingUp className="w-6 h-6" />,
      challenge: '药物分子设计需要大量虚拟筛选，本地计算资源有限。',
      solution: '采用ChainCloud云CADD平台，进行高通量虚拟筛选和分子设计。',
      result: '筛选效率提升80%，发现潜在药物分子数量增加3倍。',
      metrics: [
        { label: '筛选效率提升', value: '80%' },
        { label: '候选分子增加', value: '3倍' },
        { label: '研发成本降低', value: '45%' }
      ],
      color: 'from-green-500 to-emerald-500'
    },
    {
      industry: '基因测序',
      company: '某科研院所',
      icon: <Users className="w-6 h-6" />,
      challenge: '基因测序数据分析量大，需要强大的计算能力和存储系统。',
      solution: '构建基于ChainCloud云超算的生物信息分析流水线，实现自动化处理。',
      result: '数据处理能力提升5倍，分析准确率达到99.5%。',
      metrics: [
        { label: '处理能力提升', value: '5倍' },
        { label: '分析准确率', value: '99.5%' },
        { label: '处理时间缩短', value: '80%' }
      ],
      color: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <section className="spacing-responsive bg-white">
      <div className="container-responsive">
        {/* Header */}
        <div className="text-center mb-16 portrait:mb-10">
          <h2 className="text-responsive-lg font-bold text-gray-900 mb-4 portrait:mb-3">
            客户案例
          </h2>
          <p className="text-responsive-sm text-gray-600 max-w-3xl mx-auto">
            看看我们的客户如何通过ChainCloud云超算平台实现业务突破
          </p>
        </div>

        {/* Cases Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 portrait:grid-cols-1 gap-8 portrait:gap-5">
          {cases.map((caseItem, index) => (
            <div
              key={index}
              className="group bg-gradient-to-br from-gray-50 to-white rounded-3xl portrait:rounded-2xl p-8 portrait:p-5 portrait:md:p-6 border border-gray-100 hover:border-transparent hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6 portrait:mb-4">
                <div className="flex items-center gap-4 portrait:gap-3">
                  <div className={`w-12 h-12 portrait:w-10 portrait:h-10 bg-gradient-to-br ${caseItem.color} rounded-xl portrait:rounded-lg flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300`}>
                    <div className="portrait:scale-75">
                      {caseItem.icon}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl portrait:text-lg font-bold text-gray-900">
                      {caseItem.industry}
                    </h3>
                    <p className="text-gray-600 text-sm portrait:text-xs">
                      {caseItem.company}
                    </p>
                  </div>
                </div>
              </div>

              {/* Challenge */}
              <div className="mb-6 portrait:mb-4">
                <h4 className="font-semibold text-gray-900 mb-2 portrait:mb-1 text-base portrait:text-sm">挑战</h4>
                <p className="text-gray-700 leading-relaxed text-sm portrait:text-xs">
                  {caseItem.challenge}
                </p>
              </div>

              {/* Solution */}
              <div className="mb-6 portrait:mb-4">
                <h4 className="font-semibold text-gray-900 mb-2 portrait:mb-1 text-base portrait:text-sm">解决方案</h4>
                <p className="text-gray-700 leading-relaxed text-sm portrait:text-xs">
                  {caseItem.solution}
                </p>
              </div>

              {/* Result */}
              <div className="mb-6 portrait:mb-4">
                <h4 className="font-semibold text-gray-900 mb-2 portrait:mb-1 text-base portrait:text-sm">成果</h4>
                <p className="text-gray-700 leading-relaxed text-sm portrait:text-xs">
                  {caseItem.result}
                </p>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-3 gap-4 portrait:gap-2">
                {caseItem.metrics.map((metric, metricIndex) => (
                  <div key={metricIndex} className="text-center">
                    <div className={`text-2xl portrait:text-xl font-bold bg-gradient-to-r ${caseItem.color} bg-clip-text text-transparent`}>
                      {metric.value}
                    </div>
                    <div className="text-sm portrait:text-xs text-gray-600">
                      {metric.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16 portrait:mt-10">
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl portrait:rounded-2xl p-12 portrait:p-6 portrait:md:p-8 text-white">
            <h3 className="text-3xl portrait:text-xl portrait:md:text-2xl font-bold mb-4 portrait:mb-3">
              成为下一个成功案例
            </h3>
            <p className="text-xl portrait:text-base text-gray-300 mb-8 portrait:mb-5 max-w-2xl mx-auto">
              加入我们的客户行列，让ChainCloud助力您的业务创新和发展
            </p>
            <button
              className="bg-white text-gray-900 px-8 py-4 portrait:px-6 portrait:py-3 rounded-xl portrait:rounded-lg font-semibold text-lg portrait:text-base hover:shadow-lg transition-all duration-300 hover:scale-105"
              onClick={() => navigate('/contact')}
            >
              联系我们
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Cases; 